import React, { useState, useRef, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/context/ToastContext';
import axios from 'axios';
import { Shield, Upload, Check, AlertCircle, X } from 'lucide-react';
import { BACKEND_URL } from '@/config';
import { prepareDocument } from '@/utils/photos';
import { safeReturnPath } from '@/utils/verifications';

const API = BACKEND_URL + '/api';
axios.defaults.withCredentials = true;

const ACCEPTED = 'image/png,image/jpeg,image/webp,application/pdf';

function UploadBox({ label, hint, value, onChange, onError, dataTestid }) {
  const inputRef = useRef();
  const isImage = typeof value === 'string' && value.startsWith('data:image');
  return (
    <div
      onClick={() => !value && inputRef.current?.click()}
      style={{
        border: '2px dashed rgba(15,23,42,.15)', borderRadius: 'var(--rl)',
        padding: value ? 14 : 32, textAlign: 'center', cursor: value ? 'default' : 'pointer',
        background: value ? '#fff' : 'var(--cream)', position: 'relative', minHeight: 100,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6,
      }}
      data-testid={dataTestid}
    >
      <input ref={inputRef} type="file" accept={ACCEPTED} hidden onChange={async (e) => {
        const f = e.target.files?.[0]; if (!f) return;
        // Image compressée (2 000 px, texte lisible) ; PDF de 2 Mo au plus (T11)
        try {
          onChange(await prepareDocument(f), f);
        } catch (err) {
          onError?.(err.message);
        }
        e.target.value = '';
      }} />
      {value ? (
        <>
          {isImage ? (
            <img src={value} alt="preview" style={{ maxHeight: 110, maxWidth: '100%', borderRadius: 8, objectFit: 'contain' }} />
          ) : (
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--teal)' }}>📄 Document chargé</div>
          )}
          <button type="button" onClick={(e) => { e.stopPropagation(); onChange(null); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red)', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <X size={12} /> Retirer
          </button>
        </>
      ) : (
        <>
          <Upload size={24} style={{ color: 'var(--ink3)' }} />
          <div style={{ fontSize: 14, fontWeight: 600 }}>{label}</div>
          <div style={{ fontSize: 12, color: 'var(--ink3)' }}>{hint}</div>
        </>
      )}
    </div>
  );
}

export default function KycPage() {
  const { user, refresh } = useAuth();
  const toast = useToast();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ id_type: 'passport', id_front_data: null, id_back_data: null, selfie_data: null, address_proof_data: null });
  const [submitting, setSubmitting] = useState(false);
  const [serverStatus, setServerStatus] = useState(null);

  const kycStatus = serverStatus?.status || user?.kyc_status || 'PENDING';
  // Venu d'une action verrouillée (?retour=) : une fois la pièce envoyée, on y ramène
  const [params] = useSearchParams();
  const back = safeReturnPath(params.get('retour'));
  const backLink = back && (
    <Link to={back} className="btn btn-primary" style={{ marginTop: 20 }} data-testid="kyc-return-link">Reprendre où j’en étais</Link>
  );

  useEffect(() => {
    axios.get(`${API}/users/me/kyc`).then(r => setServerStatus(r.data)).catch(() => {});
  }, []);

  const steps = [
    { label: "Pièce d'identité", desc: 'Passeport ou carte nationale d\'identité' },
    { label: 'Selfie', desc: 'Photo de vous avec votre pièce' },
    { label: 'Justificatif (optionnel)', desc: 'Justificatif de domicile récent' },
  ];

  // Taille contrôlée par prepareDocument (photo compressée, PDF de 2 Mo au plus)
  const updateField = (key, dataUrl) => setForm(p => ({ ...p, [key]: dataUrl }));

  const handleSubmit = async () => {
    if (!form.id_front_data) { toast.error('La pièce d\'identité est obligatoire.'); return; }
    if (!form.selfie_data) { toast.error('Le selfie est obligatoire.'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/users/me/kyc`, {
        id_type: form.id_type,
        id_front_data: form.id_front_data,
        id_back_data: form.id_back_data,
        selfie_data: form.selfie_data,
        address_proof_data: form.address_proof_data,
      });
      toast.success('Documents soumis. Vérification sous 24-48h.');
      if (refresh) await refresh();
      const r = await axios.get(`${API}/users/me/kyc`);
      setServerStatus(r.data);
      setStep(3);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur lors de la soumission');
    } finally {
      setSubmitting(false);
    }
  };

  if (kycStatus === 'VERIFIED') {
    return (
      <div data-testid="kyc-page-verified">
        <div className="c2s-topbar"><div className="c2s-topbar-title">Vérification KYC</div></div>
        <div className="card" style={{ maxWidth: 500, textAlign: 'center', padding: 40 }}>
          <div style={{ width: 64, height: 64, background: 'var(--tbg)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <Check size={28} color="var(--teal)" />
          </div>
          <h3 style={{ fontFamily: 'var(--f-display)', fontSize: 20, fontWeight: 800, marginBottom: 8 }}>Identité vérifiée</h3>
          <p style={{ color: 'var(--ink2)', fontSize: 14 }}>Votre compte est vérifié. Vous bénéficiez de tous les avantages Colis2Ship.</p>
          <div style={{ marginTop: 20, display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
            <span className="badge badge-verifie">Compte vérifié</span>
          </div>
          {backLink}
        </div>
      </div>
    );
  }

  if ((kycStatus === 'IN_PROGRESS' || step === 3) && kycStatus !== 'REJECTED') {
    return (
      <div data-testid="kyc-page-pending">
        <div className="c2s-topbar"><div className="c2s-topbar-title">Vérification KYC</div></div>
        <div className="card" style={{ maxWidth: 500, textAlign: 'center', padding: 40 }}>
          <div style={{ width: 64, height: 64, background: 'var(--warm)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <Shield size={28} color="var(--gold)" />
          </div>
          <h3 style={{ fontFamily: 'var(--f-display)', fontSize: 20, fontWeight: 800, marginBottom: 8 }}>Vérification en cours</h3>
          <p style={{ color: 'var(--ink2)', fontSize: 14 }}>Vos documents sont en cours d'analyse. Vous recevrez une notification dans 24-48h.</p>
          {serverStatus?.submission?.submitted_at && (
            <p style={{ fontSize: 12, color: 'var(--ink3)', marginTop: 12 }}>
              Soumis le {new Date(serverStatus.submission.submitted_at).toLocaleString('fr-FR')}
            </p>
          )}
          {backLink}
        </div>
      </div>
    );
  }

  const rejectedReason = kycStatus === 'REJECTED' ? serverStatus?.submission?.reject_reason : null;

  return (
    <div style={{ maxWidth: 600, margin: '0 auto' }} data-testid="kyc-page">
      <div className="c2s-topbar">
        <div>
          <div className="c2s-topbar-title">Vérification KYC</div>
          <div className="c2s-topbar-sub">Requis pour les transporteurs. Permet la libération des fonds via Stripe</div>
        </div>
      </div>

      {rejectedReason && (
        <div style={{ background: '#FEF2F2', borderRadius: 'var(--r)', padding: '12px 16px', marginBottom: 16, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <AlertCircle size={18} color="#DC2626" style={{ marginTop: 2 }} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#DC2626' }}>Vérification précédente rejetée</div>
            <div style={{ fontSize: 12, color: 'var(--ink2)', marginTop: 2 }}>{rejectedReason}</div>
          </div>
        </div>
      )}

      <div style={{ background: 'var(--tbg)', borderRadius: 'var(--r)', padding: '12px 16px', marginBottom: 24, display: 'flex', gap: 10 }}>
        <Shield size={16} color="var(--teal)" style={{ marginTop: 2 }} />
        <div style={{ fontSize: 13, color: 'var(--teal)' }}>
          Vos documents sont chiffrés et stockés de manière sécurisée. Ils sont uniquement consultés par l'équipe de modération.
        </div>
      </div>

      <div className="stepper" style={{ marginBottom: 28 }}>
        {steps.map((s, i) => (
          <div key={s.label} className={`step${i < step ? ' done' : i === step ? ' active' : ''}`}>
            <div className="step-dot">{i < step ? <Check size={14} /> : i + 1}</div>
            <div className="step-label">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="card">
        <div style={{ fontFamily: 'var(--f-display)', fontSize: 16, fontWeight: 700, marginBottom: 4 }}>{steps[step]?.label}</div>
        <div style={{ fontSize: 13, color: 'var(--ink2)', marginBottom: 20 }}>{steps[step]?.desc}</div>

        {step === 0 && (
          <div>
            <div className="field-group">
              <label className="field-label">Type de document</label>
              <select className="field-select" value={form.id_type} onChange={e => setForm(p => ({ ...p, id_type: e.target.value }))} data-testid="kyc-id-type">
                <option value="passport">Passeport</option>
                <option value="id_card">Carte nationale d'identité</option>
                <option value="residence_permit">Titre de séjour</option>
              </select>
            </div>
            <UploadBox label="Pièce recto (obligatoire)" hint="JPEG, PNG ou PDF (2 Mo au plus) — les photos sont réduites automatiquement"
              value={form.id_front_data} onError={toast.error} onChange={(d, f) => updateField('id_front_data', d, f)} dataTestid="kyc-upload-id-front" />
            {form.id_type !== 'passport' && (
              <div style={{ marginTop: 12 }}>
                <UploadBox label="Pièce verso" hint="Optionnel pour les CNI"
                  value={form.id_back_data} onError={toast.error} onChange={(d, f) => updateField('id_back_data', d, f)} dataTestid="kyc-upload-id-back" />
              </div>
            )}
          </div>
        )}

        {step === 1 && (
          <UploadBox label="Selfie + document à la main"
            hint="Regardez la caméra, document bien visible"
            value={form.selfie_data} onError={toast.error} onChange={(d, f) => updateField('selfie_data', d, f)} dataTestid="kyc-upload-selfie" />
        )}

        {step === 2 && (
          <UploadBox label="Justificatif de domicile (optionnel)"
            hint="Facture EDF/eau/téléphone < 3 mois"
            value={form.address_proof_data} onError={toast.error} onChange={(d, f) => updateField('address_proof_data', d, f)} dataTestid="kyc-upload-address" />
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 24, justifyContent: 'flex-end' }}>
          {step > 0 && <button className="btn btn-ghost" onClick={() => setStep(s => s - 1)} data-testid="btn-kyc-back">← Retour</button>}
          {step < 2 ? (
            <button data-testid={`btn-kyc-next-${step}`} className="btn btn-primary"
              disabled={(step === 0 && !form.id_front_data) || (step === 1 && !form.selfie_data)}
              onClick={() => setStep(s => s + 1)}>Continuer →</button>
          ) : (
            <button data-testid="btn-kyc-submit" className="btn btn-teal"
              disabled={submitting || !form.id_front_data || !form.selfie_data}
              onClick={handleSubmit}>{submitting ? 'Envoi...' : 'Soumettre les documents'}</button>
          )}
        </div>
      </div>
    </div>
  );
}
